import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Avatar,
  Snackbar,
  Alert,
  Divider,
  InputAdornment,
  IconButton,
  Modal,
  Fade,
  Backdrop,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/Arconest_logo.png";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import google from "../../assets/google.png";
import dropdown from "../../assets/dropdown.png";

// Custom Select Icon Component - Fixed
const CustomSelectIcon = () => (
  <Box
    component="img"
    src={dropdown}
    alt="dropdown"
    sx={{
      width: 20,
      height: 20,
    }}
  />
);

const AuthRegister = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { registerUser, signInWithGoogle, authLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Testimonials data for carousel
  const testimonials = [
    {
      id: 1,
      text: "Arconest has revolutionized our project management workflow. The real-time tracking and seamless communication between engineers and clients have significantly improved our delivery timelines.",
      name: "Rajesh Deshmukh",
      role: "Project Director, BuildRight Infra"
    },
    {
      id: 2,
      text: "The field inspection tools provided by this platform are exceptional. They've eliminated manual paperwork and allowed our site engineers to focus on quality and safety.",
      name: "Sandeep Patil",
      role: "Senior Site Engineer"
    },
    {
      id: 3,
      text: "As a developer, transparency is key. Arconest provides a clear window into every phase of our construction projects, giving us the confidence we need to scale.",
      name: "Vikram Malhotra",
      role: "CEO, Urban Living Spaces"
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleNext = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  // Get data from navigation state
  const {
    email,
    name,
    role,
    isGoogleLogin,
    fromLanding,
    fromOtp,
    phone: otpPhone,
    message: otpMessage,
  } = location.state || {};

  const [formData, setFormData] = useState({
    role: role || "",
    name: name || "",
    email: email || "",
    phone: otpPhone || "",
    // CLIENT fields
    companyName: "",
    companyAddress: "",
    roleType: "",
    otherRoleType: "",
    // ENGINEER fields
    detailedAddress: "",
    city: "",
    state: "",
  });

  const [phoneError, setPhoneError] = useState("");

  // States for city/state dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // States for searchable dropdowns
  const [searchState, setSearchState] = useState("");
  const [searchCity, setSearchCity] = useState("");

  // States for modals
  const [openStateModal, setOpenStateModal] = useState(false);
  const [openCityModal, setOpenCityModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [googleLoginError, setGoogleLoginError] = useState("");

  // Fetch Indian states on component mount
  useEffect(() => {
    const fetchIndianStates = async () => {
      setLoadingStates(true);
      try {
        const response = await fetch(
          "https://countriesnow.space/api/v0.1/countries/states",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              country: "India",
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.states) {
            setStates(
              data.data.states.map((state) => ({
                value: state.name,
                label: state.name,
              }))
            );
          }
        } else {
          // Fallback states if API fails
          setStates([
            { value: "Maharashtra", label: "Maharashtra" },
            { value: "Delhi", label: "Delhi" },
            { value: "Karnataka", label: "Karnataka" },
            { value: "Tamil Nadu", label: "Tamil Nadu" },
            { value: "Uttar Pradesh", label: "Uttar Pradesh" },
            { value: "Gujarat", label: "Gujarat" },
            { value: "Rajasthan", label: "Rajasthan" },
            { value: "West Bengal", label: "West Bengal" },
          ]);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        setStates([
          { value: "Maharashtra", label: "Maharashtra" },
          { value: "Delhi", label: "Delhi" },
          { value: "Karnataka", label: "Karnataka" },
          { value: "Tamil Nadu", label: "Tamil Nadu" },
        ]);
      } finally {
        setLoadingStates(false);
      }
    };

    fetchIndianStates();
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCitiesForState = async () => {
      if (!formData.state) {
        setCities([]);
        return;
      }

      setLoadingCities(true);
      try {
        const response = await fetch(
          "https://countriesnow.space/api/v0.1/countries/state/cities",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              country: "India",
              state: formData.state,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.data) {
            setCities(
              data.data.map((city) => ({
                value: city,
                label: city,
              }))
            );
          }
        } else {
          setCities([
            { value: "Mumbai", label: "Mumbai" },
            { value: "Pune", label: "Pune" },
            { value: "Nagpur", label: "Nagpur" },
          ]);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([
          { value: "Mumbai", label: "Mumbai" },
          { value: "Pune", label: "Pune" },
        ]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCitiesForState();
  }, [formData.state]);

  // Update form when location state changes
  useEffect(() => {
    if (email) {
      setFormData((prev) => ({
        ...prev,
        email,
        name: name || "",
        role: role || "",
      }));
    }
    if (role && (fromLanding || fromOtp)) {
      setFormData((prev) => ({ ...prev, role }));
    }
    if (otpPhone) {
      setFormData((prev) => ({ ...prev, phone: otpPhone }));
    }
  }, [email, name, role, fromLanding, fromOtp, otpPhone]);

  // Phone number validation
  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Phone validation
    if (name === "phone") {
      // Only allow numbers
      const numericValue = value.replace(/\D/g, "");
      // Limit to 10 digits
      const limitedValue = numericValue.slice(0, 10);

      setFormData((prev) => ({ ...prev, [name]: limitedValue }));

      if (limitedValue && !validatePhone(limitedValue)) {
        setPhoneError("Phone number must be exactly 10 digits");
      } else {
        setPhoneError("");
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "state") {
      setFormData((prev) => ({ ...prev, city: "" }));
      setSearchCity("");
    }
  };

  // Filter states based on search
  const filteredStates = states.filter((state) =>
    state.label.toLowerCase().includes(searchState.toLowerCase())
  );

  // Filter cities based on search
  const filteredCities = cities.filter((city) =>
    city.label.toLowerCase().includes(searchCity.toLowerCase())
  );

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Phone validation
    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError("Phone number must be exactly 10 digits");
      return;
    }

    if (
      !formData.role ||
      !formData.name ||
      !formData.email ||
      !formData.phone
    ) {
      setError("Please fill all required fields");
      return;
    }

    if (!formData.email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }

    if (formData.role === "ENGINEER" && (!formData.state || !formData.city)) {
      setError("Please select state and city for Engineer");
      return;
    }

    if (formData.role === "CLIENT" && !formData.roleType) {
      setError("Please select role type for Client");
      return;
    }

    if (formData.role === "CLIENT" && formData.roleType === "Other Consultant" && !formData.otherRoleType) {
      setError("Please specify your role type");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setPhoneError("");

      let userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
      };

      if (formData.role === "CLIENT") {
        userData.companyName = formData.companyName || "";
        userData.companyAddress = formData.companyAddress || "";
        userData.roleType = formData.roleType || "";
        userData.otherRoleType = formData.otherRoleType || "";
      }

      if (formData.role === "ENGINEER") {
        userData.detailedAddress = formData.detailedAddress || "";
        userData.city = formData.city || "";
        userData.state = formData.state || "";
      }

      console.log("Sending registration data:", userData);

      // Call registerUser from auth context
      const result = await registerUser(userData);

      console.log("Registration result:", result);

      if (result && result.success) {
        if (result.pending) {
          // User needs admin approval
          setSuccess(
            result.message ||
              "Registration submitted successfully! Awaiting admin approval."
          );

          // Clear form
          setFormData({
            role: "",
            name: "",
            email: "",
            phone: "",
            companyName: "",
            companyAddress: "",
            roleType: "",
            otherRoleType: "",
            detailedAddress: "",
            city: "",
            state: "",
          });

          // Redirect to login page (user needs to wait for approval)
          setTimeout(() => {
            navigate("/login", {
              state: {
                message:
                  "Registration submitted successfully. Please wait for admin approval before logging in.",
                email: userData.email,
                isRegistrationPending: true,
              },
            });
          }, 3000);
        } else if (result.user) {
          // User is immediately active - login automatically
          setSuccess("Registration successful! Redirecting to dashboard...");

          // The registerUser function already sets userData in context
          // Navigate directly to dashboard
          setTimeout(() => {
            navigate("/dashboard");
          }, 1500);
        }
      } else {
        setError(
          result?.message || "Registration failed: No response received"
        );
      }
    } catch (err) {
      console.error("Registration error:", err);

      // Check if error is actually a success message about pending approval
      if (err.message && err.message.includes("Await admin approval")) {
        // This is actually a success
        setSuccess(
          "Registration submitted successfully! Awaiting admin approval."
        );

        // Clear form and redirect
        setFormData({
          role: "",
          name: "",
          email: "",
          phone: "",
          companyName: "",
          companyAddress: "",
          roleType: "",
          otherRoleType: "",
          detailedAddress: "",
          city: "",
          state: "",
        });

        setTimeout(() => {
          navigate("/login", {
            state: {
              message:
                "Registration submitted successfully. Please wait for admin approval before logging in.",
              email: formData.email,
              isRegistrationPending: true,
            },
          });
        }, 3000);
      } else {
        // Real error - check for specific error types
        if (err.response?.status === 409) {
          setError(err.response?.data?.message || "This email or phone number is already registered. Please use different details or try logging in.");
        } else if (err.response?.status === 500) {
          setError("Server error occurred. Please try again or contact support if the issue persists.");
        } else {
          setError(err.message || "Registration failed. Please try again.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Login from register page
  const handleGoogleLogin = async () => {
    try {
      setGoogleLoginError("");

      const selectedRole = formData.role || null;
      const result = await signInWithGoogle(selectedRole);

      if (result.exists) {
        navigate("/dashboard");
      } else {
        setFormData((prev) => ({
          ...prev,
          email: result.firebaseUser.email || prev.email,
          name: result.firebaseUser.displayName || prev.name,
          role: result.role || prev.role,
        }));

        setSuccess(
          `No account found with ${result.firebaseUser.email}. Please complete the registration form.`
        );
      }
    } catch (error) {
      setGoogleLoginError("Failed to sign in with Google. Please try again.");
      console.error("Google login error:", error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: COLORS.BACKGROUND,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccess("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!googleLoginError}
        autoHideDuration={6000}
        onClose={() => setGoogleLoginError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setGoogleLoginError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {googleLoginError}
        </Alert>
      </Snackbar>

      {/* ================= HEADER ================= */}
      <Box
        sx={{
          px: { xs: 2, md: 6 },
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center">
          <Box
            component="img"
            src={logo}
            alt="Arconest Consultants Logo"
            sx={{
              height: { xs: 28, sm: 32, md: 40, lg: 44 },
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              fontFamily: FONT_FAMILY,
              color: COLORS.TEXT,
            }}
          >
            Already have an Account:
          </Typography>
          <Button
            onClick={() => navigate("/login")}
            sx={{
              bgcolor: COLORS.SECONDARY,
              borderRadius: 1,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.H4,
              color: COLORS.BACKGROUND,
              px: { xs: 1.5, md: 2 },
              py: { xs: 0.5, md: 0.75 },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      {/* ================= MAIN CONTENT ================= */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          px: { xs: 2, md: 6 },
          py: { xs: 3, md: 4 },
          boxSizing: "border-box",
        }}
      >
        {/* ================= LEFT COLUMN ================= */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            pr: { md: 4 },
            mb: { xs: 4, md: 0 },
          }}
        >
          <Typography
            sx={{
              ...TYPOGRAPHY.H2,
              color: COLORS.SECONDARY,
              maxWidth: 420,
              mb: { xs: 3, md: 4 },
              textAlign: { xs: "center", md: "left" }, // Center on mobile, left on desktop
              width: "100%",
              mx: "auto",
            }}
          >
            #1 Web Application for Managing Client Projects
          </Typography>

          {/* Empty Space (equivalent to process box area) */}
          <Box
            sx={{
              height: {
                xs: "180px",
                sm: "220px",
                md: "280px",
                lg: "320px",
              },
              mb: { xs: 3, md: 4 },
              width: "100%",
            }}
          />

          {/* Testimonials Carousel */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { md: "90%" },
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                minHeight: "140px",
                mb: 1,
              }}
            >
              {testimonials.map((testimonial, index) => (
                <Box
                  key={testimonial.id}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: index === currentTestimonial ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                    pointerEvents:
                      index === currentTestimonial ? "auto" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      opacity: 0.8,
                      mb: { xs: 1.5, md: 2 },
                      textAlign: { xs: "center", md: "left" },
                      lineHeight: 1.6,
                    }}
                  >
                    {testimonial.text}
                  </Typography>

                  {/* Founder Section */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 24, md: 30 },
                        height: { xs: 24, md: 30 },
                      }}
                    />
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          ...TYPOGRAPHY.H4,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.PRIMARY,
                          fontWeight: 600,
                        }}
                      >
                        <strong>{testimonial.name}</strong>
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          ...TYPOGRAPHY.CAPTION_MICRO,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Carousel Controls */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mt: { xs: -3, md: 1 },
              }}
            >
              <IconButton
                onClick={handlePrev}
                size="small"
                sx={{
                  color: COLORS.PRIMARY,
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                  padding: 0.5,
                }}
              >
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>

              {/* Dots Indicator */}
              <Box display="flex" gap={1}>
                {testimonials.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor:
                        index === currentTestimonial
                          ? COLORS.PRIMARY
                          : COLORS.LIGHT_SECONDARY,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>

              <IconButton
                onClick={handleNext}
                size="small"
                sx={{
                  color: COLORS.PRIMARY,
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                  padding: 0.5,
                }}
              >
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* ================= RIGHT COLUMN ================= */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            pl: { md: 4 }, // Changed back to 4 for proper spacing
            justifyContent: "flex-start",
            alignItems: "center", // Always center on both mobile and desktop
          }}
        >
          {/* Register Form Container */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "400px" }, // Fixed max width
              display: "flex",
              flexDirection: "column",
              minHeight: "100%",
              alignItems: "center", // Center everything
            }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Box
              sx={{
                flex: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: "400px", // Fixed max width
              }}
            >
              {/* OTP Message */}
              {fromOtp && otpMessage && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "rgba(106, 17, 203, 0.1)",
                    borderRadius: 1,
                    border: `1px solid ${COLORS.PRIMARY}`,
                    textAlign: "center",
                    width: { xs: "100%", md: "294px" },
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      fontWeight: 500,
                    }}
                  >
                    {otpMessage}
                  </Typography>
                  {otpPhone && (
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        color: COLORS.TEXT,
                        mt: 1,
                      }}
                    >
                      Phone number: <strong>+91 {otpPhone}</strong> is prefilled
                      for you.
                    </Typography>
                  )}
                </Box>
              )}

              {isGoogleLogin && formData.email && (
                <Typography
                  sx={{
                    color: COLORS.TEXT,
                    ...TYPOGRAPHY.H4,
                    mb: 4,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  No account exists with{" "}
                  <span style={{ color: COLORS.SECONDARY }}>
                    {formData.email}
                  </span>
                </Typography>
              )}

              {/* Dynamic heading based on selected role */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.H2,
                  color: COLORS.PRIMARY,
                  mb: 3,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                Create Account Now
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  mb: 3,
                  width: "100%",
                  alignItems: "center",
                  maxWidth: "294px", // Fixed width for form elements
                }}
              >
                {/* REGISTER AS - FIXED PADDING */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      ...labelStyle,
                      color: COLORS.PRIMARY,
                      fontWeight: 500,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    Register as a*
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    sx={{
                      ...customSelectStyle,
                      "& .MuiOutlinedInput-root": {
                        height: "50px",
                      },
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      IconComponent: CustomSelectIcon,
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            marginTop: 1,
                            borderRadius: "6px",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                            "& .MuiMenuItem-root": {
                              ...TYPOGRAPHY.H4,
                              fontFamily: FONT_FAMILY,
                              color: COLORS.PRIMARY,
                              padding: "8px 16px", // Add proper padding to menu items
                              "&:hover": {
                                backgroundColor: "rgba(106, 17, 203, 0.1)",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "rgba(106, 17, 203, 0.15)",
                                color: COLORS.PRIMARY,
                                "&:hover": {
                                  backgroundColor: "rgba(106, 17, 203, 0.2)",
                                },
                              },
                            },
                          },
                        },
                      },
                      renderValue: (selected) => {
                        if (selected === "") {
                          return (
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                height: "50px",
                                paddingLeft: "14px",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: COLORS.LIGHT_SECONDARY,
                                  fontFamily: FONT_FAMILY,
                                  ...TYPOGRAPHY.H4,
                                }}
                              >
                                Engineer/Client
                              </Typography>
                            </Box>
                          );
                        }
                        return (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              height: "50px",
                              paddingLeft: "14px",
                            }}
                          >
                            <Typography
                              sx={{
                                color: COLORS.PRIMARY,
                                fontFamily: FONT_FAMILY,
                                ...TYPOGRAPHY.H4,
                                fontWeight: 500,
                              }}
                            >
                              {selected === "ENGINEER" ? "Engineer" : "Client"}
                            </Typography>
                          </Box>
                        );
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <Typography
                        sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontFamily: FONT_FAMILY,
                          ...TYPOGRAPHY.H4,
                        }}
                      >
                        Engineer/Client
                      </Typography>
                    </MenuItem>
                    <MenuItem value="ENGINEER">
                      <Typography
                        sx={{
                          color: COLORS.PRIMARY,
                          fontFamily: FONT_FAMILY,
                          ...TYPOGRAPHY.H4,
                          fontWeight: 500,
                        }}
                      >
                        Engineer
                      </Typography>
                    </MenuItem>
                    <MenuItem value="CLIENT">
                      <Typography
                        sx={{
                          color: COLORS.PRIMARY,
                          fontFamily: FONT_FAMILY,
                          ...TYPOGRAPHY.H4,
                          fontWeight: 500,
                        }}
                      >
                        Client
                      </Typography>
                    </MenuItem>
                  </TextField>
                </Box>

                {/* COMMON FIELDS */}
                <FormField
                  label="Name*"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />

                <FormField
                  label="Email*"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  type="email"
                  required
                  disabled={isGoogleLogin}
                />

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      ...labelStyle,
                      color: COLORS.PRIMARY,
                      fontWeight: 500,
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    Phone No*
                  </Typography>
                  <TextField
                    fullWidth
                    name="phone"
                    placeholder="Enter 10-digit Phone No"
                    value={formData.phone}
                    onChange={handleChange}
                    type="tel"
                    required
                    error={!!phoneError}
                    helperText={phoneError}
                    sx={{
                      ...customTextFieldStyle,
                      "& .MuiOutlinedInput-root": {
                        height: "50px",
                      },
                      "& .MuiFormHelperText-root": {
                        fontFamily: FONT_FAMILY,
                        ...TYPOGRAPHY.BODY_SMALL,
                        color: "#d32f2f",
                        marginLeft: 0,
                        marginRight: 0,
                        textAlign: "left",
                        marginTop: "4px",
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontFamily: FONT_FAMILY,
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        backgroundColor: "transparent",
                        height: "50px",
                        padding: "0 0px",
                        "&::placeholder": {
                          color: COLORS.LIGHT_SECONDARY,
                          opacity: 1,
                        },
                      },
                      autoComplete: "new-password",
                    }}
                  />
                </Box>

                {/* CONDITIONAL FIELDS */}
                {formData.role === "CLIENT" && (
                  <>
                    <FormField
                      label="Company Name"
                      name="companyName"
                      placeholder="Enter Company Name"
                      value={formData.companyName}
                      onChange={handleChange}
                    />
                    <FormField
                      label="Company Address"
                      name="companyAddress"
                      placeholder="Enter Company Address"
                      value={formData.companyAddress}
                      onChange={handleChange}
                    />
                    
                    {/* ROLE TYPE DROPDOWN */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        sx={{
                          ...labelStyle,
                          color: COLORS.PRIMARY,
                          fontWeight: 500,
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        Role Type*
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        name="roleType"
                        value={formData.roleType}
                        onChange={handleChange}
                        required
                        sx={{
                          ...customSelectStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "50px",
                          },
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          IconComponent: CustomSelectIcon,
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                marginTop: 1,
                                borderRadius: "6px",
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                "& .MuiMenuItem-root": {
                                  ...TYPOGRAPHY.H4,
                                  fontFamily: FONT_FAMILY,
                                  color: COLORS.PRIMARY,
                                  padding: "8px 16px",
                                  "&:hover": {
                                    backgroundColor: "rgba(106, 17, 203, 0.1)",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "rgba(106, 17, 203, 0.15)",
                                    color: COLORS.PRIMARY,
                                    "&:hover": {
                                      backgroundColor: "rgba(106, 17, 203, 0.2)",
                                    },
                                  },
                                },
                              },
                            },
                          },
                          renderValue: (selected) => {
                            if (selected === "") {
                              return (
                                <Box
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    height: "50px",
                                    paddingLeft: "14px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: COLORS.LIGHT_SECONDARY,
                                      fontFamily: FONT_FAMILY,
                                      ...TYPOGRAPHY.H4,
                                    }}
                                  >
                                    Select Role Type
                                  </Typography>
                                </Box>
                              );
                            }
                            return (
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  height: "50px",
                                  paddingLeft: "14px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: COLORS.PRIMARY,
                                    fontFamily: FONT_FAMILY,
                                    ...TYPOGRAPHY.H4,
                                    fontWeight: 500,
                                  }}
                                >
                                  {selected}
                                </Typography>
                              </Box>
                            );
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <Typography
                            sx={{
                              color: COLORS.LIGHT_SECONDARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                            }}
                          >
                            Select Role Type
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Structural Consultant">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            Structural Consultant
                          </Typography>
                        </MenuItem>
                        <MenuItem value="MEP Consultant">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            MEP Consultant
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Architect">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            Architect
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Interior Designer">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            Interior Designer
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Developer / Builder">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            Developer / Builder
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Individual">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            Individual
                          </Typography>
                        </MenuItem>
                        <MenuItem value="Other Consultant">
                          <Typography
                            sx={{
                              color: COLORS.PRIMARY,
                              fontFamily: FONT_FAMILY,
                              ...TYPOGRAPHY.H4,
                              fontWeight: 500,
                            }}
                          >
                            Other Consultant
                          </Typography>
                        </MenuItem>
                      </TextField>
                    </Box>

                    {/* OTHER ROLE TYPE TEXTBOX - CONDITIONAL */}
                    {formData.roleType === "Other Consultant" && (
                      <FormField
                        label="Specify Role Type*"
                        name="otherRoleType"
                        placeholder="Enter your role type"
                        value={formData.otherRoleType}
                        onChange={handleChange}
                        required
                      />
                    )}
                  </>
                )}

                {formData.role === "ENGINEER" && (
                  <>
                    <FormField
                      label="Detailed Address"
                      name="detailedAddress"
                      placeholder="Enter Detailed Address"
                      value={formData.detailedAddress}
                      onChange={handleChange}
                    />

                    {/* STATE DROPDOWN - FIXED PADDING */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        sx={{
                          ...labelStyle,
                          color: COLORS.PRIMARY,
                          fontWeight: 500,
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        State*
                      </Typography>
                      <TextField
                        fullWidth
                        name="state"
                        value={formData.state}
                        placeholder="Select State"
                        onClick={() => setOpenStateModal(true)}
                        sx={{
                          ...customSelectStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "50px",
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                          endAdornment: <CustomSelectIcon />,
                          sx: {
                            fontFamily: FONT_FAMILY,
                            ...TYPOGRAPHY.H4,
                            color: formData.state
                              ? COLORS.PRIMARY
                              : COLORS.LIGHT_SECONDARY,
                            height: "50px",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 14px",
                            "&::placeholder": {
                              color: COLORS.LIGHT_SECONDARY,
                              opacity: 1,
                            },
                          },
                        }}
                        disabled={loadingStates}
                      />
                    </Box>

                    {/* STATE MODAL */}
                    <Modal
                      open={openStateModal}
                      onClose={() => {
                        setOpenStateModal(false);
                        setSearchState("");
                      }}
                      closeAfterTransition
                      BackdropComponent={Backdrop}
                      BackdropProps={{
                        timeout: 500,
                      }}
                    >
                      <Fade in={openStateModal}>
                        <Box sx={modalStyle}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                              borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                              pb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.H3,
                                color: COLORS.PRIMARY,
                                fontWeight: 600,
                              }}
                            >
                              Select State
                            </Typography>
                            <IconButton
                              onClick={() => {
                                setOpenStateModal(false);
                                setSearchState("");
                              }}
                              sx={{ color: COLORS.LIGHT_SECONDARY }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>

                          {/* Search Input */}
                          <TextField
                            fullWidth
                            placeholder="Search state..."
                            value={searchState}
                            onChange={(e) => setSearchState(e.target.value)}
                            sx={{
                              mb: 2,
                              "& .MuiOutlinedInput-root": {
                                height: "44px",
                              },
                            }}
                            InputProps={{
                              sx: {
                                fontFamily: FONT_FAMILY,
                                ...TYPOGRAPHY.H4,
                                padding: "0 14px",
                              },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon
                                    sx={{ color: COLORS.LIGHT_SECONDARY }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: searchState && (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setSearchState("")}
                                    size="small"
                                    sx={{ color: COLORS.LIGHT_SECONDARY }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />

                          {/* States List */}
                          <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                            {loadingStates ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  py: 4,
                                }}
                              >
                                <CircularProgress
                                  size={30}
                                  sx={{ color: COLORS.PRIMARY }}
                                />
                              </Box>
                            ) : filteredStates.length > 0 ? (
                              filteredStates.map((state) => (
                                <MenuItem
                                  key={state.value}
                                  onClick={() => {
                                    handleChange({
                                      target: {
                                        name: "state",
                                        value: state.value,
                                      },
                                    });
                                    setOpenStateModal(false);
                                    setSearchState("");
                                  }}
                                  sx={{
                                    py: 1.5,
                                    px: 2, // Add proper padding
                                    borderBottom: `1px solid ${COLORS.BACKGROUND}`,
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(106, 17, 203, 0.1)",
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: COLORS.PRIMARY,
                                      fontFamily: FONT_FAMILY,
                                      ...TYPOGRAPHY.H4,
                                    }}
                                  >
                                    {state.label}
                                  </Typography>
                                </MenuItem>
                              ))
                            ) : (
                              <Typography
                                sx={{
                                  textAlign: "center",
                                  py: 4,
                                  color: COLORS.LIGHT_SECONDARY,
                                }}
                              >
                                No states found
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Fade>
                    </Modal>

                    {/* CITY DROPDOWN - FIXED PADDING */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        sx={{
                          ...labelStyle,
                          color: COLORS.PRIMARY,
                          fontWeight: 500,
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        City*
                      </Typography>
                      <TextField
                        fullWidth
                        name="city"
                        value={formData.city}
                        placeholder={
                          !formData.state ? "Select State First" : "Select City"
                        }
                        onClick={() => {
                          if (formData.state) setOpenCityModal(true);
                        }}
                        sx={{
                          ...customSelectStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "50px",
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                          endAdornment: <CustomSelectIcon />,
                          sx: {
                            fontFamily: FONT_FAMILY,
                            ...TYPOGRAPHY.H4,
                            color: formData.city
                              ? COLORS.PRIMARY
                              : COLORS.LIGHT_SECONDARY,
                            height: "50px",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 14px",
                            "&::placeholder": {
                              color: COLORS.LIGHT_SECONDARY,
                              opacity: 1,
                            },
                          },
                        }}
                        disabled={!formData.state || loadingCities}
                      />
                    </Box>

                    {/* CITY MODAL */}
                    <Modal
                      open={openCityModal}
                      onClose={() => {
                        setOpenCityModal(false);
                        setSearchCity("");
                      }}
                      closeAfterTransition
                      BackdropComponent={Backdrop}
                      BackdropProps={{
                        timeout: 500,
                      }}
                    >
                      <Fade in={openCityModal}>
                        <Box sx={modalStyle}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                              borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                              pb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.H3,
                                color: COLORS.PRIMARY,
                                fontWeight: 600,
                              }}
                            >
                              Select City
                            </Typography>
                            <IconButton
                              onClick={() => {
                                setOpenCityModal(false);
                                setSearchCity("");
                              }}
                              sx={{ color: COLORS.LIGHT_SECONDARY }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>

                          {/* Search Input */}
                          <TextField
                            fullWidth
                            placeholder="Search city..."
                            value={searchCity}
                            onChange={(e) => setSearchCity(e.target.value)}
                            sx={{
                              mb: 2,
                              "& .MuiOutlinedInput-root": {
                                height: "44px",
                              },
                            }}
                            InputProps={{
                              sx: {
                                fontFamily: FONT_FAMILY,
                                ...TYPOGRAPHY.H4,
                                padding: "0 14px",
                              },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon
                                    sx={{ color: COLORS.LIGHT_SECONDARY }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: searchCity && (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setSearchCity("")}
                                    size="small"
                                    sx={{ color: COLORS.LIGHT_SECONDARY }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />

                          {/* Cities List */}
                          <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                            {loadingCities ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  py: 4,
                                }}
                              >
                                <CircularProgress
                                  size={30}
                                  sx={{ color: COLORS.PRIMARY }}
                                />
                              </Box>
                            ) : filteredCities.length > 0 ? (
                              filteredCities.map((city) => (
                                <MenuItem
                                  key={city.value}
                                  onClick={() => {
                                    handleChange({
                                      target: {
                                        name: "city",
                                        value: city.value,
                                      },
                                    });
                                    setOpenCityModal(false);
                                    setSearchCity("");
                                  }}
                                  sx={{
                                    py: 1.5,
                                    px: 2, // Add proper padding
                                    borderBottom: `1px solid ${COLORS.BACKGROUND}`,
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(106, 17, 203, 0.1)",
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: COLORS.PRIMARY,
                                      fontFamily: FONT_FAMILY,
                                      ...TYPOGRAPHY.H4,
                                    }}
                                  >
                                    {city.label}
                                  </Typography>
                                </MenuItem>
                              ))
                            ) : (
                              <Typography
                                sx={{
                                  textAlign: "center",
                                  py: 4,
                                  color: COLORS.LIGHT_SECONDARY,
                                }}
                              >
                                No cities found
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Fade>
                    </Modal>
                  </>
                )}

                {/* Divider with "Or" - Updated styling */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  {/* Continue to Portal Button */}
                  <Button
                    type="submit"
                    disabled={loading || !!phoneError}
                    sx={{
                      width: "100%",
                      maxWidth: "294px",
                      height: 50,
                      bgcolor: COLORS.SECONDARY,
                      color: "#fff",
                      textTransform: "none",
                      borderRadius: "7px",
                      fontFamily: FONT_FAMILY,
                      ...TYPOGRAPHY.H4,
                      "&:hover": {
                        bgcolor: COLORS.PRIMARY,
                      },
                      "&:disabled": {
                        opacity: 0.7,
                        bgcolor: COLORS.LIGHT_SECONDARY,
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Continue to Portal"
                    )}
                  </Button>

                  {/* Divider and Google Login Option - FIXED */}
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "294px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      my: 2,
                    }}
                  >
                    <Divider
                      sx={{
                        flex: 1,
                        borderColor: COLORS.LIGHT_SECONDARY,
                        borderWidth: "0.2px",
                      }}
                    />
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.H4,
                        color: COLORS.PRIMARY,
                        mx: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Or
                    </Typography>
                    <Divider
                      sx={{
                        flex: 1,
                        borderColor: COLORS.LIGHT_SECONDARY,
                        borderWidth: "0.2px",
                      }}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={handleGoogleLogin}
                    disabled={authLoading}
                    startIcon={
                      <Box
                        component="img"
                        src={google}
                        alt="Google"
                        sx={{ width: 20, height: 20 }}
                      />
                    }
                    sx={{
                      width: "100%",
                      maxWidth: "294px",
                      height: 50,
                      borderRadius: "7px",
                      borderWidth: "0.2px",
                      textTransform: "none",
                      fontFamily: FONT_FAMILY,
                      color: COLORS.PRIMARY,
                      borderColor: COLORS.LIGHT_SECONDARY,
                      ...TYPOGRAPHY.H4,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(106, 17, 203, 0.04)",
                        borderColor: COLORS.PRIMARY,
                      },
                      "&:disabled": {
                        opacity: 0.5,
                        borderColor: COLORS.LIGHT_SECONDARY,
                      },
                    }}
                  >
                    {authLoading ? (
                      <CircularProgress
                        size={24}
                        sx={{ color: COLORS.PRIMARY }}
                      />
                    ) : (
                      "Login with Google"
                    )}
                  </Button>
                </Box>
              </Box>

              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  opacity: 0.6,
                  textAlign: "center",
                  color: COLORS.TEXT,
                  mt: 3,
                  width: "100%",
                }}
              >
                Powered by Arconest Consultants
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/* ================= REUSABLE FIELD COMPONENT ================= */
const FormField = ({
  label,
  name,
  placeholder,
  value,
  onChange,
  type = "text",
  required = false,
  disabled = false,
}) => (
  <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
    <Typography
      sx={{
        ...labelStyle,
        color: COLORS.PRIMARY,
        fontWeight: 500,
        width: "100%",
        textAlign: "left",
      }}
    >
      {label}
    </Typography>
    <TextField
      fullWidth
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      type={type}
      required={required}
      disabled={disabled}
      sx={{
        ...customTextFieldStyle,
        "& .MuiOutlinedInput-root": {
          height: "50px",
        },
      }}
      InputProps={{
        sx: {
          fontFamily: FONT_FAMILY,
          ...TYPOGRAPHY.H4,
          color: COLORS.PRIMARY,
          backgroundColor: "transparent",
          height: "50px",
          padding: "0 0px",
          "&::placeholder": {
            color: COLORS.LIGHT_SECONDARY,
            opacity: 1,
          },
        },
        autoComplete: "new-password",
      }}
    />
  </Box>
);

/* ================= STYLES ================= */
const labelStyle = {
  ...TYPOGRAPHY.H4,
  mb: "6px",
  fontFamily: FONT_FAMILY,
};

// Common base style for all inputs
const commonInputStyle = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    width: "100%",
    borderRadius: "7px",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.PRIMARY,
      borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.PRIMARY,
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.LIGHT_SECONDARY,
    borderWidth: "0.2px",
  },
  "& .MuiInputBase-input": {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    boxSizing: "border-box",
    width: "100%",
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 100px transparent inset",
      WebkitTextFillColor: COLORS.PRIMARY,
    },
    "&::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
      fontFamily: FONT_FAMILY,
      ...TYPOGRAPHY.BODY_LARGE,
    },
  },
};

// For text fields
const customTextFieldStyle = {
  ...commonInputStyle,
  "& .MuiInputBase-input": {
    ...commonInputStyle["& .MuiInputBase-input"],
    padding: "0 14px",
    height: "50px",
  },
};

// For select fields
const customSelectStyle = {
  ...commonInputStyle,
  "& .MuiSelect-select": {
    height: "50px !important",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    minHeight: "50px !important",
    padding: "0 14px !important",
  },
  "& .MuiSelect-icon": {
    color: COLORS.PRIMARY,
    right: "14px",
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 !important",
  },
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 3,
  maxHeight: "80vh",
  display: "flex",
  flexDirection: "column",
  "@media (max-width: 600px)": {
    width: "90%",
  },
};

export default AuthRegister;
